import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  Checkbox,
  Radio,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import Button from "../Button/Button";
import Select from "../Select/Select";
import TextInput from "../TextInput/TextInput";
import UserApi from "../../../../passapp-sdk/user";
import { useStore } from "../../../../hooks/useStore";
import useBreakpoint from "../../../../hooks/useBreakpoint";
import {
  paisesLatamOptions,
  PAIS_ARGENTINA_ID,
} from "../../../../utils/constants/locationConstants";
import { driverName, esTractor, VehicleInsideRow } from "../Table/utils/vehicleInsideRow";
import { requiereTractor } from "../Table/utils/exitUnitRules";
import TractorRetiraField from "./TractorRetiraField";
import type {
  ExitResponsable,
  ExitUnitSelection,
} from "../../../../views/LogisticPark/RegistroIngreso/utils/exitPrefillUtils";
import "./ExitDialog.css";

const userApi = new UserApi();

type VehicleExitDialogProps = {
  open: boolean;
  /** Unidad sobre la que se apretó "Salida". */
  row: VehicleInsideRow | null;
  /** Todas las unidades dentro, para sugerir el tractor que viene a retirar un acoplado. */
  allRows: VehicleInsideRow[];
  onClose: () => void;
  /** Devuelve quién retira y las unidades que salen, con su rol. */
  onConfirm: (responsable: ExitResponsable, selected: ExitUnitSelection[]) => void;
};

const rowLabel = (row: VehicleInsideRow) =>
  [row.vehicleTypeName, row.plate].filter(Boolean).join(" ") || "Unidad";

/**
 * Paso de selección previo al formulario de egreso: quién retira y con qué unidades.
 *
 * No escribe nada. El backend no soporta dar salida a un vehículo sin persona ni registrar "quién
 * retira", así que al confirmar se navega al formulario de egreso con la persona y las unidades
 * elegidas ya precargadas, y ese formulario es el que hace el `addEvent`.
 */
const VehicleExitDialog: React.FC<VehicleExitDialogProps> = ({
  open,
  row,
  allRows,
  onClose,
  onConfirm,
}) => {
  const { isMobile } = useBreakpoint();
  const { authStore } = useStore();

  const choferDentro = Boolean(row?.driverInside && row?.driver?.dni);
  const [retiraChofer, setRetiraChofer] = useState(true);
  const [pais, setPais] = useState<string>(PAIS_ARGENTINA_ID);
  const [documento, setDocumento] = useState("");
  const [patenteTractor, setPatenteTractor] = useState("");
  const [enganchadas, setEnganchadas] = useState<string[]>([]);
  const [errorDocumento, setErrorDocumento] = useState("");
  const [errorTractor, setErrorTractor] = useState("");
  const [buscando, setBuscando] = useState(false);

  /**
   * Las otras unidades del mismo evento que siguen dentro. Las que ya salieron no se ofrecen: el
   * backend marca cada una con su propio `inside`.
   */
  const asociadas = useMemo(
    () => (row?.associatedVehicles ?? []).filter((unit) => unit.inside && unit.plate),
    [row]
  );

  useEffect(() => {
    if (!open) return;
    setRetiraChofer(choferDentro);
    setPais(PAIS_ARGENTINA_ID);
    setDocumento("");
    setPatenteTractor("");
    setEnganchadas(asociadas.map((unit) => unit.plate));
    setErrorDocumento("");
    setErrorTractor("");
  }, [open, choferDentro, asociadas]);

  if (!row) return null;

  // Las enganchadas comparten el evento de la fila, así que llevan la misma `row`.
  const seleccionadas: ExitUnitSelection[] = [
    { plate: row.plate, role: row.role, row },
    ...asociadas
      .filter((unit) => enganchadas.includes(unit.plate))
      .map((unit) => ({ plate: unit.plate, role: unit.role, row })),
  ];

  /** Un acoplado no puede salir solo: si no hay tractor en la selección, hay que cargar uno. */
  const faltaTractorEnSeleccion = requiereTractor(seleccionadas);

  const seleccionadasPlates = seleccionadas.map((unit) => (unit.plate ?? "").toUpperCase());

  const tractoresSugeridos = allRows.filter(
    (other) => esTractor(other) && !seleccionadasPlates.includes((other.plate ?? "").toUpperCase())
  );

  const tractorDentro = tractoresSugeridos.find(
    (other) => (other.plate ?? "").toUpperCase() === patenteTractor.trim().toUpperCase()
  );

  const togglePlate = (plate: string) =>
    setEnganchadas((prev) =>
      prev.includes(plate) ? prev.filter((other) => other !== plate) : [...prev, plate]
    );

  const handleConfirm = async () => {
    // Primero los campos obligatorios —documento y tractor se marcan juntos— y recién después la
    // búsqueda del documento en el sistema.
    const documentoTrim = documento.trim();
    const faltaDocumento = !retiraChofer && !documentoTrim;
    const faltaTractor = faltaTractorEnSeleccion && !patenteTractor.trim();

    setErrorDocumento(faltaDocumento ? "Ingresá el documento de quien retira la unidad." : "");
    setErrorTractor(faltaTractor ? "Ingresá la patente del tractor que retira el acoplado." : "");
    if (faltaDocumento || faltaTractor) return;

    const selected = [...seleccionadas];
    // El tractor cargado entra siempre: si está dentro también sale del complejo, y si es externo
    // igual tiene que ser el vehículo principal del egreso —un acoplado no puede serlo—.
    if (patenteTractor.trim()) {
      selected.push({
        plate: tractorDentro?.plate ?? patenteTractor.trim().toUpperCase(),
        role: "tractor",
        row: tractorDentro,
      });
    }

    if (retiraChofer) {
      onConfirm({ tipo: "chofer" }, selected);
      return;
    }

    setBuscando(true);
    try {
      const response = await userApi.findUserByDNI(authStore.token, documentoTrim, pais);
      const user =
        response?.data && Object.keys(response.data).length > 0 ? response.data : undefined;
      onConfirm(
        { tipo: "otro", documentCountry: pais, documentNumber: documentoTrim, user },
        selected
      );
    } catch (error) {
      console.error("Error searching user for vehicle exit:", error);
      // Si no figura, se sigue igual: el alta de la persona ocurre en el formulario de egreso.
      onConfirm({ tipo: "otro", documentCountry: pais, documentNumber: documentoTrim }, selected);
    } finally {
      setBuscando(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: "ve-paper" }}
    >
      <DialogTitle disableTypography className="ve-head">
        <div>
          <h2>Registrar salida — {rowLabel(row)}</h2>
          <p>Es obligatorio asignar quién retira la unidad.</p>
        </div>
        <IconButton onClick={onClose} aria-label="Cerrar" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className="ve-body">
        <h4 className="ve-block-title">
          ¿Quién retira la unidad? <span className="ve-req">*</span>
        </h4>

        {choferDentro ? (
          <>
            <label className={retiraChofer ? "ve-opt ve-opt-sel" : "ve-opt"}>
              <Radio
                checked={retiraChofer}
                onChange={() => setRetiraChofer(true)}
                color="default"
                size="small"
              />
              <span>
                <b>Retira el chofer que ingresó</b>
                <small>
                  {driverName(row.driver)} · DNI {row.driver?.dni} — se registra también su salida
                  del complejo.
                </small>
              </span>
            </label>
            <label className={!retiraChofer ? "ve-opt ve-opt-sel" : "ve-opt"}>
              <Radio
                checked={!retiraChofer}
                onChange={() => setRetiraChofer(false)}
                color="default"
                size="small"
              />
              <span>
                <b>Retira otra persona</b>
                <small>Cargá país y documento: el sistema busca la ficha.</small>
              </span>
            </label>
          </>
        ) : (
          <div className="ve-alert">
            <WarningRoundedIcon fontSize="small" />
            <div>
              <b>El chofer que ingresó ya no está dentro del complejo.</b>
              <br />
              La unidad quedó sin chofer. Cargá el documento de quien la retira.
            </div>
          </div>
        )}

        {!retiraChofer && (
          <div className="ve-otro">
            <div className="ve-otro-grid">
              <Select
                label="País *"
                value={pais}
                onChange={(value) => setPais(String(value))}
                options={paisesLatamOptions}
                isSearchable
              />
              <TextInput
                label="N° de documento *"
                value={documento}
                onChange={(value) => {
                  setDocumento(value);
                  if (errorDocumento) setErrorDocumento("");
                }}
                extraType="numeric"
                placeholder="Ej. 30111222"
                error={errorDocumento}
              />
            </div>
            <p className="ve-hint">
              Al dar salida se busca ese documento en el sistema y se abre su ficha. Si no figura,
              se carga la persona en el formulario de egreso.
            </p>
          </div>
        )}

        {asociadas.length > 0 && (
          <div className="ve-sub">
            <h4 className="ve-block-title">Unidades enganchadas</h4>
            <p className="ve-hint">
              Vienen tildadas porque ingresaron juntas. Destildá las que quedan dentro del complejo.
            </p>
            {asociadas.map((unit) => {
              const checked = enganchadas.includes(unit.plate);
              return (
                <label key={unit.plate} className={checked ? "ve-opt ve-opt-sel" : "ve-opt"}>
                  <Checkbox
                    checked={checked}
                    onChange={() => togglePlate(unit.plate)}
                    color="default"
                    size="small"
                  />
                  <span>
                    <b>{unit.plate}</b>
                    <small>
                      {unit.role === "tractor"
                        ? "Tractor"
                        : unit.role === "trailer"
                        ? "Acoplado"
                        : "Unidad"}{" "}
                      · dentro del complejo
                    </small>
                  </span>
                </label>
              );
            })}
          </div>
        )}

        {faltaTractorEnSeleccion && (
          <TractorRetiraField
            value={patenteTractor}
            onChange={(value) => {
              setPatenteTractor(value);
              if (errorTractor) setErrorTractor("");
            }}
            error={errorTractor}
            candidates={tractoresSugeridos}
            selectedPlates={seleccionadasPlates}
          />
        )}
      </DialogContent>

      <DialogActions className="ve-foot">
        <Button variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button variant="neutral" onClick={handleConfirm} loading={buscando}>
          Dar salida
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VehicleExitDialog;
